import { DASHBOARD_RESOURCE } from 'screens/dashboard/custom-dashboards/utils/constants';
import { SortOption } from './components/SortDropdown';
import { t } from 'i18next';

/**
 * Multi-select fields that should always be treated as arrays
 */
export const MULTI_SELECT_FIELDS = [
  'type',
  'flag',
  'app',
  'publish_status',
  'section',
  'workflow_process',
  'workflow',
  'site_tag',
  'geo_tag',
  'keyword',
  'subscription_service',
  'subscription_service_type',
  'subscription_rate_id',
  'category',
  'subject',
  'industry',
  'language',
  'geography',
  'level',
];

// Search fields that can be sorted on by the api - for QP validation
export const editorialSortOptions: SortOption[] = [
  { label: 'Title', field: 'title' },
  { label: 'Priority', field: 'priority' },
  { label: 'Start time', field: 'start_time' },
  { label: 'Update time', field: 'update_time' },
  { label: 'Delete time', field: 'delete_time' },
  { label: 'Slug', field: 'slug' },
];

// Search fields that can be sorted on by the api - for QP validation
export const userSortOptions: SortOption[] = [
  { label: 'UUID', field: 'uuid' },
  { label: 'E-mail', field: 'email' },
  { label: 'First Name', field: 'first_name' },
  { label: 'Last Name', field: 'last_name' },
  { label: 'Screen Name', field: 'screen_name' },
  { label: 'Title', field: 'title' },
  { label: 'Created Date', field: 'create_time' },
];

// Search fields that can be sorted on by the api - for QP validation
export const servicesSortOptions: SortOption[] = [
  { label: 'Name', field: 'name' },
  { label: 'Type', field: 'type' },
  { label: 'Active Subs', field: 'active_users' },
  { label: 'New Subs', field: 'new_users' },
  { label: 'Stopped Subs', field: 'stopped_users' },
];

// Search fields that can be sorted on by the api
export const broadcastMessageSortOptions: SortOption[] = [
  { label: 'Title', field: 'title' },
  { label: 'Send Date', field: 'send_date' },
  { label: 'Status', field: 'send_status' },
  { label: 'Type', field: 'type' },
  { label: 'Message', field: 'message' },
  { label: 'Social Title', field: 'social_title' },
  { label: 'Social Summary', field: 'social_summary' },
  { label: 'Application', field: 'asset_app' },
];

// Search fields that can be sorted on by the api for channels
export const broadcastChannelSortOptions: SortOption[] = [
  { label: 'Name', field: 'name' },
  { label: 'ID', field: 'id' },
  { label: 'Site ID', field: 'site_id' },
];

export const transactionsSortOptions: SortOption[] = [
  { label: 'Created Date', field: 'created_at' },
  { label: 'Amount', field: 'amount' },
];

export const channelsSortOptions: SortOption[] = [
  { label: 'ID', field: 'id' },
  { label: 'Name', field: 'name' },
  { label: 'Driver', field: 'driver' },
];

export const dashboardSortOptions: SortOption[] = [
  {
    label: t('blox.dashboards.custom_dashboards.search.columns.title', 'Title'),
    field: 'title',
  },
  {
    label: t('blox.dashboards.custom_dashboards.search.columns.status', 'Status'),
    field: 'status',
  },
  {
    label: t('blox.dashboards.custom_dashboards.search.columns.created_by', 'Created By'),
    field: 'creator',
  },
];

/**
 * Default for editorial (and most common areas)
 */
export const startTimeDesc = { sort: 'start_time', dir: 'DESC' };

/**
 * Default for broadcast/message (notifier messages) search
 */
export const sendDateDesc = { sort: 'send_date', dir: 'DESC' };

/**
 * Default for pay/transactions search
 */
export const createdAtDesc = { sort: 'created_at', dir: 'DESC' };

/**
 * Default for editorial/templates search
 */
export const titleAsc = { sort: 'title', dir: 'ASC' };

/**
 * The clear/reset filters should not remove these from the filters.
 * In most cases we keep the sort/dir and query text input.
 *
 * Additionally some are currently determined in search context and
 * are not visible in the filters area, keep these as well...
 * for example `subscription`, on the user search.
 * Note: `account_type` is NOT preserved in URLs - it's detected via route context
 *
 * Define preserved keys per react-admin resource
 * to keep when using resetQpFilters helper via useSearch
 */
export const preservedKeys: Record<string, string[]> = {
  editorial: ['query', 'semantic', 'sort', 'dir'],
  user: ['query', 'sort', 'dir', 'subscription'],
  'wire/channel/search': ['channel_id'],
  'emailreach/account': ['query'],
  'emailreach/contact': ['account_id', 'query', 'sort', 'dir'],
  'emailreach/contactlist': ['account_id', 'query', 'sort', 'dir'],
  'emailreach/messages': ['account_id', 'query', 'sort', 'dir'],
  'emailreach/campaign': [
    'account_id',
    'audience_type',
    'audience_id',
    'query',
    'sort',
    'dir',
  ],
  'subscription/services': ['query', 'sort', 'dir'],
  'broadcast/message': ['query', 'sort', 'dir'],
  'broadcast/channel': ['query', 'sort', 'dir'],
  'pay/transactions': ['query', 'sort', 'dir'],
  [DASHBOARD_RESOURCE]: ['query', 'sort', 'dir', 'type'],
};
